import axios from "axios";
import { API_URL } from "../config/constants";

// Verificar si estamos en una ruta pública
const isPublicRoute = (): boolean => {
  const publicRoutes = [
    "/",
    "/login",
    "/signup",
    "/about",
    "/services",
    "/pricing",
    "/request-reset-password",
    "/reset-password",
    "/verify-email",
  ];
  return publicRoutes.some(
    (route) =>
      window.location.pathname === route ||
      window.location.pathname.startsWith(route + "/"),
  );
};

// Construye una URL manteniendo el prefijo de idioma actual (/es, /en, /fr, ...)
// para no romper el <LanguageRedirect /> del router al forzar un logout.
const SUPPORTED_LOCALES = ["es", "en", "fr", "pt", "de", "it"];
const withLocalePrefix = (path: string): string => {
  try {
    const first = window.location.pathname.split("/")[1];
    if (first && SUPPORTED_LOCALES.includes(first)) {
      return `/${first}${path}`;
    }
  } catch {
    /* noop */
  }
  return path;
};

// Crear una instancia de axios con interceptores
export const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Interceptor para añadir el token a las peticiones
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn("[AXIOS REQUEST] No se encontró token en localStorage");
    }

    // Agregar timezone del usuario en todos los requests
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      config.headers["x-user-timezone"] = userTimezone;
    } catch (error) {
      console.warn("[AXIOS REQUEST] Error al detectar timezone:", error);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor para manejar errores de sesión
axiosInstance.interceptors.response.use(
  (response) => {
    // DEBUG: Log temporal para /rag/query
    if (response.config?.url?.includes("/rag/query")) {
    }
    return response;
  },
  (error) => {
    // Manejo específico para errores de JWT signature inválida
    if (
      error.response?.status === 401 &&
      (error.response?.data?.error?.includes("Token inválido") ||
        error.response?.data?.error?.includes("invalid signature") ||
        error.response?.data?.details?.includes("JsonWebTokenError"))
    ) {
      console.warn(
        "[AXIOS] Token con signature inválida detectado - forzando logout",
      );

      // Limpiar tokens inválidos inmediatamente
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      // Solo redirigir si no estamos en una ruta pública
      if (!isPublicRoute()) {
        // Mostrar notificación al usuario
        if (window.location.pathname !== "/login") {
          alert("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
          window.location.href = withLocalePrefix("/login");
        }
      }

      return Promise.reject(error);
    }

    // Logging para debugging
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !isPublicRoute()
    ) {
      console.warn("[AXIOS] Error de autorización detectado:", {
        status: error.response?.status,
        error: error.response?.data?.error,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
